import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { fmtRelative } from "../lib/utils-app";
import { articleRegion } from "../lib/article-utils";
import Seo, { ld } from "../components/Seo";
import Img from "../components/Img";
import { Clock, FileCheck2, Sparkles, ShieldCheck } from "lucide-react";

const Card = ({ a, featured }) => (
  <Link to={`/article/${a.slug}`} className={`block io-card overflow-hidden group ${featured ? "lg:col-span-12" : ""}`} data-testid={`cat-article-${a.slug}`}>
    {a.hero_image && (
      <div className={`${featured ? "aspect-[21/9]" : "aspect-[16/9]"} overflow-hidden border-b border-[var(--io-border)]`}>
        <Img src={a.hero_image} className="w-full h-full object-cover img-hover" w={featured ? 1200 : 600} priority={!!featured} />
      </div>
    )}
    <div className="p-5">
      <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--io-muted)]">
        <span className="text-[var(--io-gold)]">{a.category}</span>
        <span>·</span>
        <span>{articleRegion(a)}</span>
        <span>·</span>
        <span>{fmtRelative(a.published_at)}</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1"><Clock size={10}/> {a.read_minutes || 4}m</span>
      </div>
      <h3 className={`font-serif ${featured ? "text-[28px] md:text-[36px]" : "text-[20px]"} leading-tight mt-2 text-[var(--io-text)] group-hover:text-[var(--io-gold)]`}>{a.headline}</h3>
      <p className="mt-2 text-[14px] text-[var(--io-text-2)] line-clamp-3">{a.summary}</p>
      <div className="mt-3 flex gap-1.5 flex-wrap">
        <span className="io-badge io-badge-blue"><Sparkles size={10}/> AI</span>
        <span className="io-badge io-badge-green"><ShieldCheck size={10}/> Reviewed</span>
        <span className="io-badge io-badge-gold"><FileCheck2 size={10}/> {(a.source_refs || []).length} src</span>
      </div>
    </div>
  </Link>
);

export default function Category() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/categories/${slug}/articles?limit=40`).then((r) => {
      setData(r.data);
      document.title = `${r.data.category} — IndiaODR`;
    }).catch(() => setData({ category: "Not Found", items: [] }));
  }, [slug]);

  if (!data) return <div className="max-w-[1380px] mx-auto px-6 py-24 text-[var(--io-muted)]">Loading…</div>;
  const [featured, ...rest] = data.items;

  return (
    <div className="max-w-[1380px] mx-auto px-4 md:px-8 py-10 md:py-14" data-testid="category-page">
      <Seo
        title={data.category}
        description={`Original, source-backed analysis on ${data.category} — curated by the IndiaODR editorial desk. ${data.total} articles indexed.`}
        path={`/category/${slug}`}
        jsonLd={[
          ld.collectionPage(
            typeof window !== "undefined" ? window.location.origin : "",
            data.category,
            `/category/${slug}`,
            `${data.total} articles in ${data.category}`,
          ),
          ld.breadcrumb(typeof window !== "undefined" ? window.location.origin : "", [
            { name: "Home", path: "/" },
            { name: "Topics", path: "/categories" },
            { name: data.category },
          ]),
        ]}
      />
      <div className="border-b border-[var(--io-border)] pb-8 mb-10 intel-grid -mx-4 md:-mx-8 px-4 md:px-8 pt-8">
        <div className="eyebrow">Intelligence Vertical</div>
        <h1 className="font-serif text-[44px] md:text-[60px] mt-2 text-[var(--io-text)]">{data.category}</h1>
        <p className="mt-3 text-[15px] text-[var(--io-text-2)] max-w-2xl">Original, source-backed analysis on {data.category.toLowerCase()} — curated by the IndiaODR editorial desk.</p>
        <div className="mt-4 flex items-center gap-3 text-[12px] font-mono text-[var(--io-muted)]">
          <span>{data.total} articles</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><span className="signal-dot"/> Live vertical</span>
        </div>
      </div>

      {data.items.length === 0 && <div className="py-20 text-center text-[var(--io-muted)]" data-testid="category-empty">No articles in this vertical yet.</div>}

      {featured && (
        <div className="mb-8" data-testid="category-featured">
          <Card a={featured} featured />
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {rest.map((a) => <Card key={a.id} a={a} />)}
      </div>
    </div>
  );
}
